import * as yup from 'yup';

// Mirrors ShortDescriptionRules.cs in BevNET.API, the server rule every job flow shares, so
// change the two together. The server tests (ShortDescriptionRulesTests.cs) run the same cases
// as shortDescription.test.ts.

export const SHORT_DESCRIPTION_MAX_LENGTH = 100;
export const REQUIRED_MESSAGE = 'The Short Description field is required.';
export const LINE_BREAK_MESSAGE = 'Please keep the Short Description on one line.';
export const WEB_ADDRESS_MESSAGE = 'Please enter a Short Description without a web address.';
export const EMAIL_ADDRESS_MESSAGE = 'Please enter a Short Description without an email address.';
export const MAX_LENGTH_MESSAGE = 'Please keep the Short Description to 100 characters or fewer.';

// Only addresses that start with http://, https:// or ftp://. A bare "acme.com" is allowed,
// because plenty of real company names look like that.
const WEB_ADDRESS = /(?:https?|ftp):\/\//i;

// Include Unicode letters, marks and numbers so internationalized addresses cannot bypass
// the rule. A handle such as "@acme" has no domain, so it is allowed. Using a RegExp
// constructor keeps the Unicode flag compatible with this package's ES5 TypeScript target.
const EMAIL_ADDRESS = new RegExp('[\\p{L}\\p{M}\\p{N}._%+-]+@[\\p{L}\\p{M}\\p{N}.-]+\\.[\\p{L}\\p{M}]{2,}', 'u');

const LINE_BREAK = /[\r\n]/;
// .NET treats U+0085 as whitespace while JavaScript trim does not. Check both sets after Yup's
// trim so a whitespace-only value has the same required error in the SPA and API.
const BLANK = /^[\s\u0085]*$/;

export const hasLineBreak = (value?: string | null): boolean => !!value && LINE_BREAK.test(value);

export const containsWebAddress = (value?: string | null): boolean => !!value && WEB_ADDRESS.test(value);

export const containsEmailAddress = (value?: string | null): boolean => !!value && EMAIL_ADDRESS.test(value);

/** Each run of line breaks, with the spaces around it, becomes one space. */
export const replaceLineBreaks = (value: string): string => value.replace(/\s*[\r\n]+\s*/g, ' ');

export const shortDescriptionSchema = yup.string().nullable().trim()
  .required(REQUIRED_MESSAGE)
  .test('not-blank', REQUIRED_MESSAGE, (value) => !value || !BLANK.test(value))
  .test('one-line', LINE_BREAK_MESSAGE, (value) => !hasLineBreak(value))
  .test('no-web-address', WEB_ADDRESS_MESSAGE, (value) => !containsWebAddress(value))
  .test('no-email-address', EMAIL_ADDRESS_MESSAGE, (value) => !containsEmailAddress(value))
  .max(SHORT_DESCRIPTION_MAX_LENGTH, MAX_LENGTH_MESSAGE);

/** The message for the first rule `value` breaks, or null when it passes. */
export const getShortDescriptionError = (value?: string | null): string | null => {
  try {
    shortDescriptionSchema.validateSync(value);
    return null;
  } catch (e) {
    return (e as yup.ValidationError).message;
  }
};
